import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Calendar, Clock } from 'lucide-react';

export default function SessionHistory({ sessions, onRefresh }) {
  const [expandedSessions, setExpandedSessions] = useState(new Set());

  const toggleSession = (sessionId) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded?.has(sessionId)) {
      newExpanded?.delete(sessionId);
    } else {
      newExpanded?.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getToolLabel = (toolType) => {
    const labels = {
      'breath_coach': '🫁 Breath Coach',
      'pitch_coach': '🎵 Pitch Coach',
      'audition_song': '🎤 Audition Song'
    };
    return labels?.[toolType] || toolType;
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-500/20 text-green-300 border-green-500/30',
      'in_progress': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      'planned': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'skipped': 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    return colors?.[status] || colors?.planned;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 75) return 'text-blue-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (sessions?.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-8">
        <div className="text-center text-white/60">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-white/40" />
          <h3 className="text-xl font-semibold text-white mb-2">No Practice Sessions Yet</h3>
          <p>Start a practice session to see your history and analytics here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Session History
        </h2>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
        >
          Refresh
        </button>
      </div>
      <div className="space-y-3">
        {sessions?.map((session) => (
          <div
            key={session?.id}
            className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden"
          >
            {/* Session Header */}
            <div
              onClick={() => toggleSession(session?.id)}
              className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">
                      {session?.sessionName}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(session?.status)}`}>
                      {session?.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-white/60">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(session?.createdAt)}
                    </span>
                    {session?.totalDurationSeconds && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(session?.totalDurationSeconds)}
                      </span>
                    )}
                    {session?.workflow && (
                      <span className="text-purple-300">
                        {session?.workflow?.name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {session?.status === 'completed' && (
                    <>
                      <div className="text-right">
                        <div className="text-sm text-white/60">Completion</div>
                        <div className="text-lg font-bold text-white">
                          {Math.round(session?.completionRate)}%
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-white/60">Score</div>
                        <div className={`text-lg font-bold ${getScoreColor(session?.overallScore)}`}>
                          {Math.round(session?.overallScore)}%
                        </div>
                      </div>
                    </>
                  )}
                  {expandedSessions?.has(session?.id) ? (
                    <ChevronUp className="w-5 h-5 text-white/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/60" />
                  )}
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedSessions?.has(session?.id) && session?.segments && session?.segments?.length > 0 && (
              <div className="border-t border-white/10 p-4 bg-white/5">
                <h4 className="text-sm font-semibold text-white/80 mb-3">
                  Segment Performance
                </h4>
                <div className="space-y-2">
                  {session?.segments?.map((segment) => (
                    <div
                      key={segment?.id}
                      className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-white/40 text-sm">
                          #{segment?.segmentOrder}
                        </span>
                        <span className="text-white font-medium">
                          {getToolLabel(segment?.toolType)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        {segment?.durationSeconds && (
                          <span className="text-white/60 text-sm">
                            {formatDuration(segment?.durationSeconds)}
                          </span>
                        )}
                        {segment?.performanceScore > 0 && (
                          <span className={`font-semibold ${getScoreColor(segment?.performanceScore)}`}>
                            {Math.round(segment?.performanceScore)}%
                          </span>
                        )}
                        {segment?.completedAt ? (
                          <span className="text-green-400 text-sm">✓ Complete</span>
                        ) : (
                          <span className="text-yellow-400 text-sm">In Progress</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {session?.notes && (
                  <div className="mt-4 p-3 bg-white/5 rounded-lg">
                    <h5 className="text-sm font-semibold text-white/80 mb-2">Notes</h5>
                    <p className="text-white/70 text-sm">{session?.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}